import { FC, memo, useCallback, useEffect, useState } from 'react';
import { deleteStation, getLine, updateStations } from "../../../api/shuttleSetting.api";
import useAxios from "../../../hooks/useAxios";
import { IGetLine } from "../../../models/shuttleSetting.model";
import { ShuttleContents } from "../../../pages/campus/shuttle";
import Button from '../../../components/Button';
import { AiOutlineDownSquare, AiOutlineUpSquare } from "react-icons/ai";
import styled from 'styled-components';
import _ from "lodash";
import EditLocationModal from "./EditLocation";
import Loader from "../../Loader";
import ErrorResponse from "../../ErrorResponse";
import useNotification from "../../../hooks/useNotification";
import Map from "../../Map";
import { Button as AntButton, Modal } from 'antd';
import ConfirmModal from "../../modals/ConfirmModal";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuth from "../../../hooks/useAuth";
import Split from "react-split";
import TableEmpty from "../../common/TableEmpty";

const Block = styled.div`
  .button {
    & > * {
      font-size: 26px;
      line-height: 26px;
    }

    .disable {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

interface IProps {
  lineCode: number | undefined;
}


const ShuttleLineInput = memo(({ value, onChange }: any) => {

  return (
    <input
      type="text"
      className="form-control input-text"
      value={value}
      onChange={onChange}
    />
  )
});

const LineEdit: FC<IProps> = ({
  lineCode
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetLine>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [isEdit, setIsEdit] = useState(false);
  const [stations, setStations] = useState<any>([]);
  const [editLocationVisible, setEditLocationVisible] = useState(false);
  const [stationIndex, setStationIndex] = useState(-1);
  const [coord, setCoord] = useState<string[]>([]);
  const [modalKeyword, setModalKeyword] = useState('');
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [stationCode, setStationCode] = useState(-1);
  const [useMapSetting, setUseMapSetting] = useState(false);

  useEffect(() => {
    doGetLine();
  }, [lineCode]);

  useEffect(() => {
    if (res && !isLoading) {
      const _stations = _.cloneDeep(res.data.stationInfo);
      _stations.forEach(item => item.stationStatus = 'update');
      setStations(_stations);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      if (lineCode) execute(getLine(currentCampus, lineCode));
      setIsEdit(false);
      successNotification(t('common.message.edit1'));
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      successNotification(t('common.message.delete'));
      const index = stations.findIndex((item: any) => item.stationCode === stationCode);
      const _stations = [...stations];
      _stations.splice(index, 1);
      _stations.forEach((item, i) => item.seq = i + 1);
      setStations(_stations);
    }
  }, [deleteRes, deleteIsLoading]);

  const doGetLine = () => {
    if (lineCode) execute(getLine(currentCampus, lineCode));
  }

  const sortStations = (stations: any) => stations.sort((a: any, b: any) => a.seq < b.seq ? -1 : (a.seq > b.seq ? 1 : 0));

  const raiseStation = (seq: number) => {
    const _stations = [...stations];
    const index = _stations.findIndex(item => item.seq === seq);
    _stations[index].seq--;
    _stations[index - 1].seq++;
    setStations(sortStations(_stations));
    setUseMapSetting(true);
  }

  const lowerStation = (seq: number) => {
    const _stations = [...stations];
    const index = _stations.findIndex(item => item.seq === seq);
    _stations[index].seq++;
    _stations[index + 1].seq--;
    setStations(sortStations(_stations));
    setUseMapSetting(true);
  }

  const onAddStation = useCallback(() => {
    const _stations = [...stations];
    _stations.push({
      stationStatus: 'create',
      stationName: '',
      latitude: '',
      longitude: '',
      passengers: 0,
      seq: _stations.length + 1,
    });
    setStations(_stations);
  }, [stations])

  const onDeleteStation = (index: number) => {
    if (stations[index].passengers > 0) {
      return infoNotification(t('campus.shuttle.message10'));
    }

    const _stations = [...stations];
    const [station] = _stations.splice(index, 1);

    if (station.hasOwnProperty('stationCode')) {
      setStationCode(station.stationCode);
      setConfirmDeleteVisible(true);
    } else {
      _stations.forEach((item, i) => item.seq = i + 1);
      setStations(_stations);
    }
  }

  const doDeleteStation = () => {
    if (!lineCode) return;
    deleteExecute(deleteStation(currentCampus, lineCode, stationCode));
    setConfirmDeleteVisible(false);
  }

  const onSetStationName = useCallback((index: number, value: string) => {
    setStations((prevInputs: any) => {
      const newInputs = [...prevInputs];
      newInputs[index] = { ...newInputs[index], stationName: value };
      return newInputs
    })
  }, []);

  const onSubmit = () => {
    if (!lineCode) return;

    for (let i = 0; i < stations.length; i++) {
      if (!stations[i].stationName) {
        return infoNotification(`${t('campus.shuttle.text1')}${i + 1}${t('campus.shuttle.text2')}`);
      }
      if (!stations[i].latitude || !stations[i].longitude) {
        return infoNotification(`${t('campus.shuttle.text3')}${i + 1}${t('campus.shuttle.text4')}`);
      }
    }

    submitExecute(updateStations(currentCampus, lineCode, stations));
  }

  const onMapModalSubmit = useCallback(() => {
    const lat = sessionStorage.getItem('lat');
    const lng = sessionStorage.getItem('lng');

    if (!lat || !lng) return;

    setStations((prevInputs: any) => {
      const newInputs = [...prevInputs];
      newInputs[stationIndex] = { ...newInputs[stationIndex], latitude: lat, longitude: lng};
      return newInputs;
    });

    onMapModalInit();
  }, [stationIndex]);

  const onMapModalInit = () => {
    setModalKeyword('');
    setEditLocationVisible(false);
    setUseMapSetting(true);
  }

  return (
    <Block>
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={deleteError} />

      <div className="comm-bx__space">
        <h3
          className="pointer sub-title"
          onClick={() => {
            setCoord([]);
            setStationIndex(-1);
          }}
        >
          {res?.data.lineName || ''}
        </h3>
      </div>
      <Split
        className="splite-divide"
        sizes={[50, 50]}
        minSize={100}
        expandToMin={false}
        gutterSize={10}
        gutterAlign="center"
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        cursor="col-resize"
      >
        <div className="col-frame left_panel">
          {/* Left Side */}
          {stations.length === 0 ?
            (
              <TableEmpty text={t('campus.shuttle.text5')} className={'mt-0'} ></TableEmpty>
            ) :
            (
              <>
                <div className="comm-table__area mt-0">
                  <table>
                    <thead>
                      <tr>
                        <th className="text-center">{t('campus.shuttle.column18')}</th>
                        <th className="text-center">{t('campus.shuttle.column19')}</th>
                        <th className="text-center">{t('campus.shuttle.column20')}</th>
                        <th className="text-center">{t('campus.shuttle.column21')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        isEdit
                          // edit mode
                          ? (
                            <>
                              {stations.map((item: any, index: number) => (
                                <tr key={index}>
                                  <td>
                                    <ShuttleLineInput
                                      key={`bus-line-${index}`}
                                      value={item.stationName}
                                      onChange={(e:any) => onSetStationName(index, e.target.value)}
                                    />
                                    {/*<input*/}
                                    {/*  type="text"*/}
                                    {/*  className="form-control input-text"*/}
                                    {/*  value={item.stationName}*/}
                                    {/*  onChange={e => onSetStationName(index, e.target.value)}*/}
                                    {/*/>*/}
                                  </td>
                                  <td className="text-center">
                                    <div className="comm-btn__area justify-content-center">
                                      <Button
                                        className="btn-basic small mint"
                                        onClick={() => {
                                          setStationIndex(index);
                                          setEditLocationVisible(true);
                                        }}
                                        text={t('campus.shuttle.button2')}
                                      />
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <div className="comm-btn__area justify-content-center">
                                      <Button
                                        className="btn-basic small gray"
                                        onClick={() => onDeleteStation(index)}
                                        text={t('common.button.delete')}
                                        disabled={item.passengers > 0}
                                        writeAuthYn={writeAuthYn}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="order-sett__area">
                                      <a
                                        className={item.seq === 1 ? 'btn-order up disabled' : 'btn-order up'}
                                        onClick={() => {
                                          if (item.seq === 1) return;
                                          raiseStation(item.seq);
                                        }}
                                      />
                                      <a
                                        className={item.seq === stations.length ? 'btn-order down disabled' : 'btn-order down'}
                                        onClick={() => {
                                          if (item.seq === stations.length) return;
                                          lowerStation(item.seq);
                                        }}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </>
                          )
                          // read mode
                          : stations.length === 0
                            ? (
                              <tr>
                                <td
                                  className="text-center"
                                  colSpan={4}
                                >
                                  {t('campus.shuttle.text5')}
                                </td>
                              </tr>
                            )
                            : stations.map((item: any, index: number) => (
                              <tr
                                className={stationIndex === index ? 'chosen' : 'pointer'}
                                key={index}
                                onClick={() => {
                                  setStationIndex(index);
                                  setCoord([item.latitude, item.longitude]);
                                }}
                              >
                                <td className="text-center">
                                  {item.stationName}
                                </td>
                                <td className="text-center">
                                  <div className="comm-btn__area justify-content-center">
                                    <Button
                                      className="btn-basic small mint"
                                      text={t('campus.shuttle.button2')}
                                      disabled
                                    />
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="comm-btn__area justify-content-center">
                                    <Button
                                      className="btn-basic small gray"
                                      text={t('common.button.delete')}
                                      disabled
                                    />
                                  </div>
                                </td>
                                <td className="center">
                                  <div className="order-sett__area">
                                    <a className={'btn-order up disabled'} />
                                    <a className={'btn-order down disabled'} />
                                  </div>
                                </td>
                              </tr>
                            ))
                      }
                    </tbody>
                  </table>
                </div>
              </>
            )
          }
          {
            isEdit
            // edit mode
            ? <div className="comm-bx__between">
                <div className="comm-btn__area ml-auto">
                  <Button
                    className="btn-basic pink-line"
                    onClick={onAddStation}
                    text={t('common.button.add')}
                  />
                </div>
              </div>
            :
            <div className="comm-bx__space">
              <div className="comm-btn__area">
                <Button
                  className="btn-basic gray-line"
                  onClick={() => history.push(`/campus/shuttle?index=${ShuttleContents.LineList}`)}
                  text={t('common.button.list')}
                />
              </div>
            </div>
          }
        </div>
        <div className="col-frame right_panel">
          {/* Right Side */}
          <div className="comm-table__area rowType mt-0">
            <table>
              <tbody>
                <tr>
                  <th className="text-center">{t('campus.shuttle.column22')}</th>
                </tr>
                <tr>
                  {
                    coord.length === 0
                      ? (
                        <td>
                          <Map
                            address={`${res?.data.address || ''} ${res?.data.detailAddress || ''}`}
                            useMapSetting={useMapSetting}
                            setUseMapSetting={setUseMapSetting}
                            stations={stations}
                          />
                        </td>
                      )
                      : (
                        <td>
                          <Map
                            coord={coord}
                            useMapSetting={useMapSetting}
                            setUseMapSetting={setUseMapSetting}
                          />
                        </td>
                      )
                  }
                </tr>
              </tbody>
            </table>
          </div>
          <div className="comm-bx__space">
            <div className="comm-btn__area right">
            {
              isEdit
                // edit mode
                ? (
                  <>
                    <Button
                      className="btn-basic pink"
                      onClick={onSubmit}
                      text={t('common.button.save')}
                    />
                    <Button
                      className="btn-basic gray"
                      onClick={() => {
                        doGetLine();
                        setIsEdit(false);
                      }}
                      text={t('common.button.cancel')}
                    />
                  </>
                )
                // read mode
                : (
                  <Button
                    className="btn-basic pink-line"
                    onClick={() => setIsEdit(true)}
                    text={t('common.button.edit')}
                    writeAuthYn={writeAuthYn}
                  />
                )
            }
            </div>
        </div>

        </div>
      </Split>


      <ConfirmModal
        visible={confirmDeleteVisible}
        setVisible={setConfirmDeleteVisible}
        onConfirm={doDeleteStation}
        titleText={t('campus.shuttle.title4')}
        messageText={t('campus.shuttle.text6')}
        confirmText={t('common.button.delete')}
        cancelText={t('common.button.cancel')}
      />

      <Modal
        title={t('campus.shuttle.title5')}
        visible={editLocationVisible}
        width={648}
        onCancel={() => setEditLocationVisible(false)}
        destroyOnClose
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onMapModalSubmit}
            >
              {t('common.button.save')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => setEditLocationVisible(false)}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <EditLocationModal
          stationIndex={stationIndex}
          stations={stations}
          keyword={modalKeyword}
          setKeyword={setModalKeyword}
          address={`${res?.data.address || ''} ${res?.data.detailAddress || ''}`}
        />
      </Modal>
    </Block>
  );
}

export default memo(LineEdit);

